// Iter 110 · Slice 1 (P1.D) — Employee Self-Service Comp-Off page.
// Two panels:
//  • Left: New comp-off-credit request form (qualifying_date, source, days, remarks)
//  • Right: My comp-off requests (Pending / Approved / Rejected / Cancelled)
//  • Bottom: Expiring credits (within 30 days) — surfaces from the ledger.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarBlank, PlusCircle, WarningCircle, ClockCounterClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const SOURCE_LABELS = {
    weekly_off_work: "Worked on weekly-off",
    holiday_work: "Worked on public holiday",
    manual_hr_grant: "HR manual grant",
    other: "Other (specify in remarks)",
};

const STATUS_TONE = {
    Pending: "bg-amber-100 text-amber-800",
    Approved: "bg-emerald-100 text-emerald-800",
    Rejected: "bg-rose-100 text-rose-800",
    Cancelled: "bg-slate-100 text-slate-600",
    Expired: "bg-orange-100 text-orange-800",
    Consumed: "bg-indigo-100 text-indigo-800",
};

export default function CompOffPage() {
    const [requests, setRequests] = useState([]);
    const [expiring, setExpiring] = useState([]);
    const [draft, setDraft] = useState({
        qualifying_date: "",
        source: "weekly_off_work",
        days: 1.0,
        remarks: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [reqResp, expResp] = await Promise.all([
                api.get("/leave/comp-off-request/list", { params: { scope: "mine", status_filter: "all", page_size: 50 } }),
                api.get("/leave/comp-off/expiring", { params: { within_days: 30 } }),
            ]);
            setRequests(reqResp.data.items || []);
            setExpiring(expResp.data.items || []);
        } catch (err) {
            // Missing linked employee is a valid state — surface without noise
            const status = err?.response?.status;
            if (status !== 400) {
                toast.error(formatApiError(err, "Failed to load comp-off data"));
            }
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const submit = async () => {
        if (!draft.qualifying_date) { toast.error("Qualifying date is required."); return; }
        if (!draft.remarks || draft.remarks.trim().length < 10) {
            toast.error("Remarks are required (min 10 characters).");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/leave/comp-off-request", {
                leave_type_code: "COMP_OFF",
                qualifying_date: draft.qualifying_date,
                source: draft.source,
                days: Number(draft.days),
                remarks: draft.remarks.trim(),
            });
            toast.success("Comp-off request submitted — your manager will be notified.");
            setDraft({ qualifying_date: "", source: "weekly_off_work", days: 1.0, remarks: "" });
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Comp-off request failed"));
        } finally {
            setSubmitting(false);
        }
    };

    const cancel = async (rid) => {
        if (!window.confirm("Cancel this comp-off request?")) return;
        try {
            await api.post(`/leave/comp-off-request/${rid}/cancel`, { remarks: "cancelled by requester" });
            toast.success("Request cancelled");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Cancel failed"));
        }
    };

    const totalPending = useMemo(() => requests.filter((r) => r.status === "Pending").length, [requests]);
    const totalApproved = useMemo(() => requests.filter((r) => r.status === "Approved").length, [requests]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="comp-off-page">
            <PageHeader
                eyebrow="Employee Self-Service"
                title="Compensatory Off"
                description="Request comp-off credits for work performed on weekly-offs or public holidays. Approved credits expire after 90 days (per policy)."
                actions={(
                    <Link to="/leave/portal" className="text-xs text-indigo-600 hover:underline">
                        Back to Leave Portal →
                    </Link>
                )}
            />

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left · Request form */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-1" data-testid="co-request-form">
                    <div className="flex items-center gap-2 mb-4">
                        <PlusCircle size={20} className="text-teal-600" />
                        <h3 className="text-sm font-semibold text-slate-800">Request a comp-off credit</h3>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-slate-600">Qualifying work date</label>
                            <Input
                                type="date"
                                data-testid="co-qualifying-date"
                                value={draft.qualifying_date}
                                onChange={(e) => setDraft({ ...draft, qualifying_date: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">Source</label>
                            <select
                                data-testid="co-source"
                                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                value={draft.source}
                                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                            >
                                {Object.entries(SOURCE_LABELS)
                                    .filter(([k]) => k !== "manual_hr_grant")
                                    .map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">Days</label>
                            <Input
                                type="number" step="0.5" min="0.5" max="2"
                                data-testid="co-days"
                                value={draft.days}
                                onChange={(e) => setDraft({ ...draft, days: Number(e.target.value) })}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600">Remarks (required, min 10 chars)</label>
                            <Textarea
                                data-testid="co-remarks"
                                rows={3}
                                placeholder="Briefly explain the work performed"
                                value={draft.remarks}
                                onChange={(e) => setDraft({ ...draft, remarks: e.target.value })}
                            />
                        </div>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            className="w-full"
                            data-testid="co-submit"
                        >
                            {submitting ? "Submitting…" : "Submit for manager approval"}
                        </Button>
                    </div>
                </div>

                {/* Right · Requests list */}
                <div className="bg-white rounded-xl border border-slate-200 lg:col-span-2" data-testid="co-request-list">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CalendarBlank size={18} className="text-slate-500" />
                            <h3 className="text-sm font-semibold text-slate-800">My comp-off requests</h3>
                        </div>
                        <div className="flex gap-2 text-xs">
                            <Badge className="bg-amber-100 text-amber-800">{totalPending} pending</Badge>
                            <Badge className="bg-emerald-100 text-emerald-800">{totalApproved} approved</Badge>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">ID</th>
                                    <th className="text-left px-3 py-2 font-medium">Qualifying date</th>
                                    <th className="text-left px-3 py-2 font-medium">Source</th>
                                    <th className="text-right px-3 py-2 font-medium">Days</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-left px-3 py-2 font-medium">Submitted</th>
                                    <th className="text-right px-3 py-2 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.length === 0 && (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-400">No comp-off requests yet.</td></tr>
                                )}
                                {requests.map((r) => (
                                    <tr key={r.id} className="border-t border-slate-100" data-testid={`co-request-row-${r.id}`}>
                                        <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.id}</td>
                                        <td className="px-3 py-2 text-xs text-slate-700">{formatDate(r.qualifying_date)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{SOURCE_LABELS[r.source] || r.source}</td>
                                        <td className="px-3 py-2 text-xs text-right font-mono">{Number(r.days).toFixed(2)}</td>
                                        <td className="px-3 py-2">
                                            <Badge className={`text-xs ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status}</Badge>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(r.created_at)}</td>
                                        <td className="px-3 py-2 text-right">
                                            {r.status === "Pending" && (
                                                <Button
                                                    variant="ghost" size="sm"
                                                    onClick={() => cancel(r.id)}
                                                    data-testid={`co-cancel-${r.id}`}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Bottom · Expiring credits */}
            <div className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="co-expiring-panel">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                    <WarningCircle size={18} className="text-orange-600" />
                    <h3 className="text-sm font-semibold text-slate-800">Comp-off credits expiring within 30 days</h3>
                </div>
                {expiring.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">No credits expiring soon.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Credit ID</th>
                                    <th className="text-left px-3 py-2 font-medium">Qualifying date</th>
                                    <th className="text-right px-3 py-2 font-medium">Days</th>
                                    <th className="text-left px-3 py-2 font-medium">Expires on</th>
                                    <th className="text-left px-3 py-2 font-medium">Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expiring.map((e) => (
                                    <tr key={e.id} className="border-t border-slate-100" data-testid={`co-expiring-row-${e.id}`}>
                                        <td className="px-3 py-2 text-xs font-mono text-slate-500">{e.id}</td>
                                        <td className="px-3 py-2 text-xs text-slate-700">{formatDate(e.extra?.qualifying_date)}</td>
                                        <td className="px-3 py-2 text-xs text-right font-mono text-emerald-700">+{Number(e.delta_days).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs text-orange-700 font-semibold">
                                            <ClockCounterClockwise size={12} className="inline mr-1" />
                                            {formatDate(e.extra?.expires_at)}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{SOURCE_LABELS[e.extra?.source] || e.extra?.source || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
